import React, { useRef, useState } from 'react'
import {
  Row,
  Column,
  Tabs,
  TabHeader,
  TabsContents,
  TabsHeaders,
  GridContainer,
  Typography,
  ResponsiveImage,
  Section,
  H2
} from '@vp/swan'

import StockOptionContent from './StockOptionContent'

import { stylesheet } from './StockOptionSection.scss'
import { trackTabSelected } from '../../utils/tracking/tracking'
import { useStyles } from '@vp/ubik-context'
import { useAppContext } from '../../state/AppContext'
import { StockOptionDataModel } from './types'
import { transformStockOptionTabImage } from '../AutoGenerate/cloudinaryImageTransformer'
import { DebugPageSections } from '../debugComponents/DebugPageSections'

const StockOptions = (props: { stockOptionData: StockOptionDataModel | null }) => {
  const { stockOptionData } = props

  useStyles(stylesheet)
  const ref = useRef<HTMLDivElement>(null)
  const { merchandisingCategoryId, dictionaryEntries } = useAppContext()
  const [selectedTabId, setSelectedTabId] = useState<string | null>('1')
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(new Set(['1']))

  const textLabels = {
    highlights: dictionaryEntries?.['stockOptionsHighlights'] ?? '',
    uploadCta: dictionaryEntries?.['stockOptionsUploadDesignCTA'] ?? '',
    viewDetails: dictionaryEntries?.['stockOptionsViewFullDetails'] ?? '',
    browseDesigns: dictionaryEntries?.['stockOptionsBrowseDesigns'] ?? '',
    outOfStock: dictionaryEntries?.['stockOptionsOutOfStock'] ?? ''
  }

  const handleTabChange = (tabId: string | null) => {
    if (tabId) {
      setSelectedTabId(tabId)
      setVisitedTabs((prev) => new Set(prev).add(tabId))
      sessionStorage.setItem('stockOptionsTabId', tabId)
    }
  }

  if (!stockOptionData) return null

  return (
    <>
      <GridContainer paddingX={0}>
        <Section marginBottom={3}>
          <H2 data-testid="sectionTitle" fontSize="x2large">
            {stockOptionData.title}
          </H2>
          <Typography data-testid="sectionDescription" fontSize="standard">
            {stockOptionData.subtitle}
          </Typography>
        </Section>
        <Row>
          <Column span={12}>
            <Tabs
              data-testid="stockOptionsTabs"
              selectedTabId={selectedTabId}
              onRequestTabChange={(requestedTabId) => {
                trackTabSelected({
                  categoryId: merchandisingCategoryId,
                  pageZone: 'stockOptions',
                  tabIndex: requestedTabId
                })
                handleTabChange(requestedTabId)
              }}
            >
              <TabsHeaders ref={ref} data-testid="stockOptionsTabsHeaders">
                {stockOptionData.items?.map((item, tabIndex) => {
                  return (
                    <TabHeader
                      tabId={`${tabIndex + 1}`}
                      key={item.mpvId}
                      data-testid="stockOptionsTabItem"
                    >
                      <ResponsiveImage
                        data-testid={`stockOptionsTabImage-${item.name}`}
                        className="tabimage-border"
                        src={transformStockOptionTabImage(item.image.src)}
                        height="64"
                        alt={`${item.name}`}
                      />
                      <br />
                      <Typography fontSize="small" data-testid="merchandisingName">
                        {item.name}
                      </Typography>
                    </TabHeader>
                  )
                })}
              </TabsHeaders>
              <TabsContents data-testid="stockOptionsTabsContents">
                {stockOptionData.items.map((item, tabIndex: number) => {
                  const currentTabId = `${tabIndex + 1}`
                  const isActiveTab = selectedTabId === currentTabId
                  return (
                    <StockOptionContent
                      key={item.mpvId}
                      mpvId={item.mpvId}
                      item={item}
                      browseDesign={item.browseDesignsUrl}
                      browseDesignCta={textLabels.browseDesigns}
                      uploadDesignCta={textLabels.uploadCta}
                      viewDetailsCta={textLabels.viewDetails}
                      highlightsSectionTitle={textLabels.highlights}
                      tabIndex={tabIndex}
                      outOfStockContent={textLabels.outOfStock}
                      isActiveTab={isActiveTab}
                      hasBeenVisited={visitedTabs.has(currentTabId)}
                      currentTabId={currentTabId}
                    />
                  )
                })}
              </TabsContents>
            </Tabs>
          </Column>
        </Row>
      </GridContainer>
      <DebugPageSections
        rawSectionData={stockOptionData ?? {}}
        resolvedSectionData={stockOptionData}
      />
    </>
  )
}

export default StockOptions
